import { AppModule } from 'app/module';
import { WatchlistButton } from 'module/watchlistButton/main';
import { WatchlistLoginInfo } from 'module/watchlistLoginInfo/main';
import { DislikeButton } from 'module/dislikeButton/main';
import { ShowMemoButton } from 'module/showMemoButton/main';
import localforage from 'localforage';
import { VISITED_ID_CACHE_KEY } from 'app/storageKeys';
import { KEYCODE_ENTER } from 'app/keyCodes';
import Template from './template.hbs';
import './styles.scss';

export class JobsListItem extends AppModule {
    setTemplate() {
        this.template = Template;
        if (module.hot) {
            module.hot.accept('./template.hbs', () => {
                this.template = Template;
                this.render();
            });
        }
    }

    domBindings() {
        return {
            watchlist: '.m-jobsListItem__watchlist .m-watchlistButton',
            dislike: '.m-jobsListItem__dislike .m-dislikeButton',
            watchlistLoginInfo: '.m-jobsListItem__watchlistLoginInfo .m-watchlistLoginInfo',
            pushedJob: '.m-jobsListItem__labelPushedJob',
            bsLabel: '.m-jobsListItem__bsLabel',
            brandingPill: '.m-jobsListItem__bsLabel--branding',
            brandingSolutionPill: '.m-jobsListItem__bsLabel--brandingSolution',
            imageContainer: '.m-jobsListItem__imageContainer',
            logo: '.m-jobsListItem__logo',
            title: '.m-jobsListItem__title',
            titleLink: '.m-jobsListItem__titleLink',
            companyLink: '.m-jobsListItem__companyName--link',
            companyName: '.m-jobsListItem__companyName',
            locations: ['.m-jobsListItem__location'],
            showMemoButton: '.m-jobsListItem__showMemoButton .m-showMemoButton',
            newBadge: '.m-jobsListItem__newBadge',
            smartApplyBadge: '.m-jobsListItem__smartApplyBadge',
        };
    }

    subEvents() {
        if (this.watchlistLoginInfo) {
            this.watchlistLoginInfo.events.on('onCloseButtonClicked', (e) => this.closeWatchlistLoginInfo(e));
        }
        if (this.showMemoButton) {
            this.showMemoButton.events.on('openMemoTab', () => {
                this.events.emit('openMemoTab', this.props.id);
            });
        }
    }

    subs() {
        if (this.dom.watchlist) { // if inactive jobs ...
            this.watchlistButton = new WatchlistButton(this.dom.watchlist);
        }
        if (this.dom.watchlistLoginInfo) {
            this.watchlistLoginInfo = new WatchlistLoginInfo(this.dom.watchlistLoginInfo);
        }
        if (this.dom.dislike) {
            this.dislike = new DislikeButton(this.dom.dislike);
        }
        if (this.dom.showMemoButton) {
            this.showMemoButton = new ShowMemoButton(this.dom.showMemoButton);
        }
    }

    subsProps() {
        if (this.watchlistButton) {
            this.watchlistButton.updateProps({ id: this.props.id });
        }
        if (this.watchlistLoginInfo) {
            this.watchlistLoginInfo.updateProps({ id: this.props.id });
        }
        if (this.dislike) {
            this.dislike.updateProps({ id: this.props.id });
        }
    }

    domEvents() {
        if (this.dom.companyLink) {
            this.dom.companyLink.addEventListener('click', (e) => e.stopPropagation());
        }

        this.dom.titleLink?.addEventListener?.('keydown', (e) => {
            if (!(e.key === KEYCODE_ENTER)) return;
            e.preventDefault();
            e.stopPropagation();
            window.open(`/jobs/${this.props.id}`, '_blank');
        });
        this.dom.el.addEventListener('click', (e) => {
            if (this.props.isSplitView) {
                if (e && !e.metaKey && !e.ctrlKey) {
                    e.preventDefault();
                    this.events.emit('jobClick', this.props.id);
                } else if (e && (e.metaKey || e.ctrlKey) && (e.target !== this.dom.titleLink)) {
                    window.open(`/jobs/${this.props.id}`);
                }
            }

            this.handleNewBadgeBehavior();
        });

        this.dom.el.addEventListener('auxclick', () => {
            this.handleNewBadgeBehavior();
        });
    }

    async handleNewBadgeBehavior() {
        if (this.props.showNewBadge) {
            this.disableNewBadge();

            if (!await this.existsInBadgeCache()) {
                this.addToBadgeCache();
            }
        }
    }

    closeWatchlistLoginInfo() {
        if (this.watchlistLoginInfo) {
            this.watchlistLoginInfo.updateProps({ visible: false }, true);
        }
    }

    getPropsFromDom() {
        return {
            title: this.dom.title ? this.dom.title.innerText : '',
            id: this.dom.el.getAttribute('data-id'),
            companyId: this.dom.el.getAttribute('data-company-id'),
            isSplitView: this.dom.el.classList.contains('m-jobsListItem--isSplitView'),
            isSimilarJob: (this.dom.el.getAttribute('data-is-similar-job') === 'true'),
            isWatchlist: (this.dom.el.getAttribute('data-is-watchlist') === 'true'),
            isSmartApplyJob: !!this.dom.smartApplyBadge,
            pushedJob: !!this.dom.pushedJob,
            pushedJobEvent: this.dom.pushedJob ? this.dom.pushedJob.getAttribute('data-ga-event') : false,
            trackPage: this.dom.el.getAttribute('data-page'),
            showNewBadge: !!this.dom.newBadge,
            isActive: this.dom.el.getAttribute('data-isActive') === 'true',
            company: {
                isBrandingSolution: !!this.dom.brandingSolutionPill,
                isChiffre: !!this.dom.el.classList.contains('m-jobsListItem--isChiffre'),
                hasBranding: !!this.dom.brandingPill,
                name: this.dom.companyName ? this.dom.companyName.innerText : '',
            },
        };
    }

    updateShowMemoButtonState(showMemoButton) {
        this.showMemoButton.updateProps({ active: showMemoButton }, true);
    }

    async addToBadgeCache() {
        const cache = await localforage.getItem(VISITED_ID_CACHE_KEY) ?? [];
        const newCacheEntry = {
            id: this.props.id,
            date: new Date(),
        };
        cache.push(newCacheEntry);
        await localforage.setItem(VISITED_ID_CACHE_KEY, cache);
    }

    async existsInBadgeCache() {
        const cache = await localforage.getItem(VISITED_ID_CACHE_KEY) ?? [];
        return cache.find((entry) => entry.id === this.props.id);
    }

    disableNewBadge() {
        this.updateProps({ showNewBadge: false });
        this.hideNewBadge();
    }

    hasNewBadge() {
        return this.props.showNewBadge;
    }

    hideNewBadge() {
        if (this.dom.newBadge) {
            this.dom.newBadge.classList.add('m-jobsListItem__newBadge--hidden');
        }
    }

    async fadeInNewBadge() {
        if (this.dom.newBadge) {
            this.dom.newBadge.classList.remove('m-jobsListItem__newBadge--hidden');
            this.dom.newBadge.classList.add('m-jobsListItem__newBadge--fadeIn');
        }
        if (await this.existsInBadgeCache()) {
            this.disableNewBadge();
        }
    }
}
